import {
  getDocumentById,
  getCollection,
  setDocById,
  addDocToCollection,
  updateDocById,
  deleteDocByCollectionAndId,
} from '../_shared/firestore/index.js';
import { DowntimeModel } from '@cmms/models';
import { createCMMSView } from '../views/views.js';
import { getAuth } from 'firebase/auth';

const COLLECTION_NAME = 'downtimes';

// Helper function to get current user's company ID
async function getCurrentUserCompanyId(db) {
  const auth = getAuth(db.app);
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  const userDoc = await getDocumentById(db, 'users', currentUser.uid);
  
  if (!userDoc || !userDoc.company_id) {
    throw new Error('User company not found');
  }

  return userDoc.company_id;
}

// Frontend function: Get all downtimes (filtered by company)
async function getDowntimes(db) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    
    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'company_id', operator: '==', value: companyId }
    ]);
    
    const downtimes = docs.map((doc) => {
      return { id: doc.$id, model: new DowntimeModel(doc) };
    });
    return createCMMSView({ data: downtimes });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get downtime by ID
async function getDowntimeById(db, downtimeId) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    const doc = await getDocumentById(db, COLLECTION_NAME, downtimeId);
    if (!doc) return createCMMSView({ error: 'Downtime not found' });
    if (doc.company_id !== companyId) {
      return createCMMSView({ error: 'Access denied: Downtime not found' });
    }
    const downtime = new DowntimeModel(doc);
    return createCMMSView({ data: { id: doc.$id, model: downtime } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get downtimes by company
async function getDowntimesByCompany(db, companyId) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'company_id',
      operator: '==',
      value: companyId,
    });

    const downtimes = docs.map((doc) => {
      return { id: doc.$id, model: new DowntimeModel(doc) };
    });

    return createCMMSView({ data: downtimes });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get downtimes by asset
async function getDowntimesByAsset(db, assetId) {
  try {
    const companyId = await getCurrentUserCompanyId(db);

    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'asset_id', operator: '==', value: assetId },
      { key: 'company_id', operator: '==', value: companyId }
    ]);

    const downtimes = docs.map((doc) => {
      return { id: doc.$id, model: new DowntimeModel(doc) };
    });

    return createCMMSView({ data: downtimes });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get downtimes by work order
async function getDowntimesByWorkOrder(db, workOrderId, companyId = null) {
  try {
    // Auto-get companyId if not provided
    if (!companyId) {
      companyId = await getCurrentUserCompanyId(db);
    }

    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'wo_id', operator: '==', value: workOrderId },
      { key: 'company_id', operator: '==', value: companyId }
    ]);

    const downtimes = docs.map((doc) => {
      return { id: doc.$id, model: new DowntimeModel(doc) };
    });

    return createCMMSView({ data: downtimes });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get active downtimes (ended_at is null)
async function getActiveDowntimes(db) {
  try {
    const companyId = await getCurrentUserCompanyId(db);

    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'ended_at', operator: '==', value: null },
      { key: 'company_id', operator: '==', value: companyId }
    ]);

    const downtimes = docs.map((doc) => {
      return { id: doc.$id, model: new DowntimeModel(doc) };
    });

    return createCMMSView({ data: downtimes });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get completed downtimes (ended_at is not null)
async function getCompletedDowntimes(db) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    
    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'company_id', operator: '==', value: companyId }
    ]);
    
    const completedDowntimes = docs
      .filter((doc) => doc.ended_at !== null)
      .map((doc) => {
        return { id: doc.$id, model: new DowntimeModel(doc) };
      });

    return createCMMSView({ data: completedDowntimes });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Create new downtime
async function createDowntime(db, downtimeData) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    const downtimeWithCompany = {
      ...downtimeData,
      company_id: companyId
    };
    
    const downtime = new DowntimeModel(downtimeWithCompany);
    const downtimeJson = downtime.toJSON();

    const docRef = await addDocToCollection(db, COLLECTION_NAME, downtimeJson);

    return createCMMSView({ data: docRef });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Update downtime
async function updateDowntime(db, downtimeId, updateData) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    
    // First get the existing downtime data
    const existingDowntime = await getDocumentById(db, COLLECTION_NAME, downtimeId);

    if (!existingDowntime) {
      return createCMMSView({ error: 'Downtime not found' });
    }

    // Check company access
    if (existingDowntime.company_id !== companyId) {
      return createCMMSView({ error: 'Access denied: Downtime not found' });
    }

    // Merge existing data with update data, ensuring all required fields are present
    const mergedData = {
      ...existingDowntime,
      ...updateData,
      // Ensure required fields are always present
      downtime_id: existingDowntime.downtime_id || downtimeId,
      asset_id: existingDowntime.asset_id || '',
      wo_id: existingDowntime.wo_id || '',
      company_id: existingDowntime.company_id || companyId,
      started_at: existingDowntime.started_at || Date.now(),
    };

    let downtime;
    try {
      downtime = new DowntimeModel(mergedData);
    } catch (modelError) {
      return createCMMSView({
        error: `Failed to create DowntimeModel: ${modelError.message}`,
      });
    }

    const docRef = await setDocById(
      db,
      COLLECTION_NAME,
      downtimeId,
      downtime.toJSON()
    );

    return createCMMSView({ data: { id: downtimeId, ...updateData } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Delete downtime
async function deleteDowntime(db, downtimeId) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    
    // First get the existing downtime data to check company access
    const existingDowntime = await getDocumentById(db, COLLECTION_NAME, downtimeId);
    
    if (!existingDowntime) {
      return createCMMSView({ error: 'Downtime not found' });
    }
    
    // Check company access
    if (existingDowntime.company_id !== companyId) {
      return createCMMSView({ error: 'Access denied: Downtime not found' });
    }
    
    await deleteDocByCollectionAndId(db, COLLECTION_NAME, downtimeId);
    return createCMMSView({ data: { id: downtimeId, deleted: true } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Export frontend functions
export {
  getDowntimes,
  getDowntimeById,
  getDowntimesByCompany,
  getDowntimesByAsset,
  getDowntimesByWorkOrder,
  getActiveDowntimes,
  getCompletedDowntimes,
  createDowntime,
  updateDowntime,
  deleteDowntime,
};
